import { Component, TemplateRef, ViewChild } from '@angular/core';
import { NbDialogService } from '@nebular/theme';
import { Apollo } from 'apollo-angular';
import { DynamicListViewComponent } from '../../../shared/component/dynamiclistview/dynamiclistview.component';
import { EquipmentCountingJournalBatchCreateComponent } from '../components/equipment-counting-journal-batch-create/equipment-counting-journal-batch-create.component';
import { JournalDetailsComponent } from '../components/journal-details/journal-details.component';
import { WindowService } from '../../../@core/services/window.service';
import { MUTATION_DELETE_EQUIPMENT_COUNTING_JOURNAL } from '../../../@core/graphql/mutations/equipment/deleteEquipmentCountingJournal.mutation';
import { QUERY_GetEquipmentCountingJournalShareLink } from '../../../@core/graphql/queries/equipment/getEquipmentCountingJournalShareLink.query';
import { MUTATION_CANCEL_EQUIPMENT_COUNTING_JOURNAL } from '../../../@core/graphql/mutations/equipment/cancelEquipmentCountingJournal.mutation';
import { MUTATION_RUN_EQUIPMENT_COUNTING_JOURNAL_VARIANCE } from '../../../@core/graphql/mutations/equipment/runEquipmentCountingJournalVariance.mutation';
import { MUTATION_COMPLETE_EQUIPMENT_COUNTING_JOURNAL } from '../../../@core/graphql/mutations/equipment/completeEquipmentCountingJournal.mutation';
import { GridMenuButton } from '../../../shared/component/dynamiclistview/components/grid-menu/grid-menu.component';

@Component({
    selector: 'stack-mono-equipment-counting-journal-list',
    templateUrl: './equipment-counting-journal-list.component.html',
    styleUrls: ['./equipment-counting-journal-list.component.scss'],
    standalone: false
})
export class EquipmentCountingJournalListComponent {
  @ViewChild('journalList') journalList: DynamicListViewComponent;

  filters: any[] = [];
  selectedJournals: any[] = [];
  metricsTabClicked = false;
  isDeletingJournals = false;
  isCancellingJournals = false;
  isRunningVariance = false;
  isCompletingJournals = false;
  isLoadingShareLink = false;
  shareLinkURL: string | null = null;
  shareLinkCopied = false;
  batchShareLinkURL: string | null = null;
  batchShareLinkCopied = false;
  buttons: GridMenuButton[] = [
    {
      label: 'Create Journals',
      icon: 'fa-plus',
      action: 'new',
      type: 'button',
    },
    {
      label: 'Delete',
      icon: 'fa-trash',
      action: 'delete',
      type: 'button',
      iconColor: 'red',
    },
    {
      label: 'Cancel',
      icon: 'fa-ban',
      type: 'button',
      iconColor: '#ff3d71',
    },
    {
      label: 'Run Variance',
      icon: 'fa-exclamation-triangle',
      type: 'button',
      iconColor: 'orange',
    },
    {
      label: 'Complete Count',
      icon: 'fa-check',
      type: 'button',
      iconColor: '#00d68f',
    },
    {
      label: 'Share',
      icon: 'fa-share',
      type: 'button',
    },
  ];

  // Statuses that can be cancelled (In Progress only - Recount can only be completed)
  private cancellableStatuses = ['InProgress'];

  // Statuses that can be deleted (Ready only)
  private deletableStatuses = ['Ready'];

  // Statuses that can run variance (InProgress or Ready)
  private varianceStatuses = ['InProgress', 'Ready'];

  // Statuses that can be completed (Recount only)
  private completableStatuses = ['Recount'];

  constructor(
    private windowService: WindowService,
    private dialogService: NbDialogService,
    private apollo: Apollo,
  ) {}

  onTabChange(event: any) {
    // Nebular's changeTab event contains the tab object with tabTitle
    const tabTitle = event?.tab?.tabTitle || event?.tabTitle || '';
    // If metrics tab is clicked, mark it as clicked
    if (tabTitle === 'Metrics') {
      this.metricsTabClicked = true;
    }
  }

  onJournalSelect(journals: any[]) {
    this.selectedJournals = journals || [];
  }

  batchCreateJournal() {
    const windowRef = this.windowService.openWindow(
      EquipmentCountingJournalBatchCreateComponent,
      {
        options: {
          title: 'Batch Create Equipment Counting Journal',
          windowClass: 'detail-window',
        },
      },
    );

    // Listen for window close and refresh grid if needed
    windowRef.onClose.subscribe((result: any) => {
      if (result?.refresh) {
        // Refresh immediately
        this.journalList.refreshGrid();

        // Refresh again after 5 seconds to catch newly created journals
        setTimeout(() => {
          this.journalList.refreshGrid();
        }, 5000);
      }
    });
  }

  /**
   * Returns the journals that can be deleted (Ready or Cancelled status)
   */
  getDeletableJournals(): any[] {
    return this.selectedJournals.filter((journal) =>
      this.deletableStatuses.includes(journal.status),
    );
  }

  /**
   * Returns the count of journals that can be deleted (Ready or Cancelled status)
   */
  getDeletableJournalsCount(): number {
    return this.getDeletableJournals().length;
  }

  /**
   * Check if delete button should be enabled
   * Only enabled when there are selected journals with deletable status
   */
  canDeleteSelected(): boolean {
    return this.getDeletableJournalsCount() > 0;
  }

  /**
   * Returns the journals that can be cancelled (Ready, In Progress, or Recount status)
   */
  getCancellableJournals(): any[] {
    return this.selectedJournals.filter((journal) =>
      this.cancellableStatuses.includes(journal.status),
    );
  }

  /**
   * Returns the count of journals that can be cancelled
   */
  getCancellableJournalsCount(): number {
    return this.getCancellableJournals().length;
  }

  /**
   * Check if cancel button should be enabled
   * Only enabled when there are selected journals with cancellable status
   */
  canCancelSelected(): boolean {
    return this.getCancellableJournalsCount() > 0;
  }

  /**
   * Opens the delete confirmation dialog
   */
  deleteSelectedJournalsConfirm(dialog: TemplateRef<any>) {
    if (!this.canDeleteSelected()) {
      return;
    }
    this.dialogService.open(dialog, {
      hasBackdrop: false,
      closeOnBackdropClick: false,
    });
  }

  /**
   * Deletes only the journals that are in deletable status (Ready or Cancelled)
   */
  async deleteSelectedJournals(ref?: any) {
    const deletableJournals = this.getDeletableJournals();

    if (deletableJournals.length === 0) {
      ref?.close();
      return;
    }

    this.isDeletingJournals = true;

    try {
      for (const journal of deletableJournals) {
        await this.apollo
          .mutate({
            mutation: MUTATION_DELETE_EQUIPMENT_COUNTING_JOURNAL,
            variables: { id: journal.id },
          })
          .toPromise();
      }

      this.selectedJournals = [];
      this.journalList.refreshGrid();
    } finally {
      this.isDeletingJournals = false;
      ref?.close();
    }
  }

  /**
   * Share button is enabled when exactly one journal is selected
   */
  canShareSelected(): boolean {
    return this.selectedJournals.length === 1;
  }

  /**
   * Opens the share dialog and fetches a fresh external share link
   * for the selected journal (mirrors the service-visit share link).
   */
  shareSelectedJournal(dialog: TemplateRef<any>) {
    if (!this.canShareSelected()) {
      return;
    }

    const journal = this.selectedJournals[0];
    this.shareLinkURL = null;
    this.shareLinkCopied = false;
    this.batchShareLinkURL = null;
    this.batchShareLinkCopied = false;
    this.isLoadingShareLink = true;

    this.dialogService.open(dialog, {
      hasBackdrop: false,
      closeOnBackdropClick: false,
    });

    this.apollo
      .query<any>({
        query: QUERY_GetEquipmentCountingJournalShareLink,
        variables: { id: journal.id },
        fetchPolicy: 'network-only',
      })
      .subscribe({
        next: (result) => {
          this.shareLinkURL =
            result?.data?.getEquipmentCountingJournal?.shareLinkURL || null;
          this.batchShareLinkURL =
            result?.data?.getEquipmentCountingJournal?.batchShareLinkURL ||
            null;
          this.isLoadingShareLink = false;
        },
        error: () => {
          this.isLoadingShareLink = false;
        },
      });
  }

  /**
   * Copies the share link to the clipboard
   */
  copyShareLink() {
    if (!this.shareLinkURL) {
      return;
    }
    navigator.clipboard.writeText(this.shareLinkURL);
    this.shareLinkCopied = true;
    this.batchShareLinkCopied = false;
  }

  /**
   * Copies the batch share link (all journals from the batch combined)
   */
  copyBatchShareLink() {
    if (!this.batchShareLinkURL) {
      return;
    }
    navigator.clipboard.writeText(this.batchShareLinkURL);
    this.batchShareLinkCopied = true;
    this.shareLinkCopied = false;
  }

  /**
   * Opens the cancel confirmation dialog
   */
  cancelSelectedJournalsConfirm(dialog: TemplateRef<any>) {
    if (!this.canCancelSelected()) {
      return;
    }
    this.dialogService.open(dialog, {
      hasBackdrop: false,
      closeOnBackdropClick: false,
    });
  }

  /**
   * Cancels only the journals that are in cancellable status
   */
  async cancelSelectedJournals(ref?: any) {
    const cancellableJournals = this.getCancellableJournals();

    if (cancellableJournals.length === 0) {
      ref?.close();
      return;
    }

    this.isCancellingJournals = true;

    try {
      for (const journal of cancellableJournals) {
        await this.apollo
          .mutate({
            mutation: MUTATION_CANCEL_EQUIPMENT_COUNTING_JOURNAL,
            variables: {
              dto: {
                id: journal.id,
                status: 'Cancelled',
              },
            },
          })
          .toPromise();
      }

      this.selectedJournals = [];
      this.journalList.refreshGrid();
    } finally {
      this.isCancellingJournals = false;
      ref?.close();
    }
  }

  /**
   * Returns the journals that can run variance (InProgress or Ready status)
   */
  getVarianceJournals(): any[] {
    return this.selectedJournals.filter((journal) =>
      this.varianceStatuses.includes(journal.status),
    );
  }

  /**
   * Returns the count of journals that can run variance
   */
  getVarianceJournalsCount(): number {
    return this.getVarianceJournals().length;
  }

  /**
   * Check if run variance button should be enabled
   * Only enabled when there are selected journals with variance-eligible status
   */
  canRunVarianceSelected(): boolean {
    return this.getVarianceJournalsCount() > 0;
  }

  /**
   * Opens the run variance confirmation dialog
   */
  runVarianceSelectedJournalsConfirm(dialog: TemplateRef<any>) {
    if (!this.canRunVarianceSelected()) {
      return;
    }
    this.dialogService.open(dialog, {
      hasBackdrop: false,
      closeOnBackdropClick: false,
    });
  }

  /**
   * Runs variance on the selected journals
   */
  async runVarianceSelectedJournals(ref?: any) {
    const varianceJournals = this.getVarianceJournals();

    if (varianceJournals.length === 0) {
      ref?.close();
      return;
    }

    this.isRunningVariance = true;

    try {
      for (const journal of varianceJournals) {
        await this.apollo
          .mutate({
            mutation: MUTATION_RUN_EQUIPMENT_COUNTING_JOURNAL_VARIANCE,
            variables: { id: journal.id },
          })
          .toPromise();
      }

      this.selectedJournals = [];
      this.journalList.refreshGrid();
    } finally {
      this.isRunningVariance = false;
      ref?.close();
    }
  }

  /**
   * Returns the journals that can be completed (Recount status)
   */
  getCompletableJournals(): any[] {
    return this.selectedJournals.filter((journal) =>
      this.completableStatuses.includes(journal.status),
    );
  }

  /**
   * Returns the count of journals that can be completed
   */
  getCompletableJournalsCount(): number {
    return this.getCompletableJournals().length;
  }

  /**
   * Check if complete button should be enabled
   * Only enabled when there are selected journals with completable status
   */
  canCompleteSelected(): boolean {
    return this.getCompletableJournalsCount() > 0;
  }

  /**
   * Opens the complete confirmation dialog
   */
  completeSelectedJournalsConfirm(dialog: TemplateRef<any>) {
    if (!this.canCompleteSelected()) {
      return;
    }
    this.dialogService.open(dialog, {
      hasBackdrop: false,
      closeOnBackdropClick: false,
    });
  }

  /**
   * Completes the selected journals
   */
  async completeSelectedJournals(ref?: any) {
    const completableJournals = this.getCompletableJournals();

    if (completableJournals.length === 0) {
      ref?.close();
      return;
    }

    this.isCompletingJournals = true;

    try {
      for (const journal of completableJournals) {
        await this.apollo
          .mutate({
            mutation: MUTATION_COMPLETE_EQUIPMENT_COUNTING_JOURNAL,
            variables: { id: journal.id },
          })
          .toPromise();
      }

      this.selectedJournals = [];
      this.journalList.refreshGrid();
    } finally {
      this.isCompletingJournals = false;
      ref?.close();
    }
  }

  viewJournalDetail(journal: any) {
    // Open a window with the journal details component showing KPIs and lines
    const windowRef = this.windowService.openWindow(JournalDetailsComponent, {
      context: {
        journalId: journal.id,
        journalName: journal.name || journal.id,
        journalStatus: journal.status,
      },
      objectId: journal.id,
      options: {
        title: `Journal Details - ${journal.name || journal.id}`,
        windowClass: 'detail-window',
      },
    });

    // Listen for window close and refresh grid if needed
    windowRef.onClose.subscribe((result: any) => {
      if (result?.refresh) {
        this.journalList.refreshGrid();
      }
    });
  }
}
